function toPositiveInt(value, fallback = null) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalizeTenantType(value) {
  const normalized = String(value || '').trim().toLowerCase()
  if (['business', 'enterprise', 'company', 'org'].includes(normalized)) {
    return 'business'
  }
  if (['personal', 'private', 'individual', 'user'].includes(normalized)) {
    return 'personal'
  }
  return ''
}

function normalizeAccountType(value) {
  const normalized = String(value || '').trim().toLowerCase()
  if (['superuser', 'system_admin', '__superuser__'].includes(normalized)) return 'superuser'
  if (['enterprise_admin', 'business_admin', 'company_admin', '__enterprise_admin__'].includes(normalized)) return 'enterprise_admin'
  if (['enterprise_sub_user', 'enterprise_user', 'business_user', 'company_user'].includes(normalized)) {
    return 'enterprise_sub_user'
  }
  if (['personal_independent', 'personal', 'independent', 'individual'].includes(normalized)) {
    return 'personal_independent'
  }
  return ''
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase())
  }
  return false
}

export function normalizeUserInfo(userInfo = {}) {
  const normalized = { ...(userInfo || {}) }
  const userId = toPositiveInt(normalized.user_id ?? normalized.userId ?? normalized.id)
  const tenantId = toPositiveInt(normalized.tenant_id ?? normalized.tenantId ?? normalized.tenant?.id)
  // 【2026-10 部门智能体授权】前端的 department_id 供 ai-service
  // list_agents / create_conversation / chat_with_agent 拼进 query/body 使用；
  // userInfo 可能来自 /users/me（已有 department_id）或 token。
  const departmentId = toPositiveInt(
    normalized.department_id ?? normalized.departmentId
  )

  normalized.id = userId
  normalized.user_id = userId
  normalized.userId = userId
  normalized.tenant_id = tenantId
  normalized.tenantId = tenantId
  normalized.department_id = departmentId
  normalized.departmentId = departmentId

  // 保留 shared_knowledge_base 字段（如果不存在则默认为 false）。
  // 注意：JS 默认参数 userInfo = {} 只对 undefined 生效，对 null 不生效。
  // 原先用 'shared_knowledge_base' in userInfo 会拋 TypeError（Cannot use 'in'
  // operator to search for ... in null），导致 userInfo 为 null 时整个
  // resolveUserScope 拋错，进而让 userScope computed 返回 undefined、渲染层崩溃。
  // 这里改为先判空再 hasOwnProperty，确保 null/undefined 输入也能安全返回。
  if (userInfo && Object.prototype.hasOwnProperty.call(userInfo, 'shared_knowledge_base')) {
    normalized.shared_knowledge_base = userInfo.shared_knowledge_base
  } else {
    normalized.shared_knowledge_base = false
  }

  return normalized
}

export function resolveUserScope(userInfo = {}) {
  const normalized = normalizeUserInfo(userInfo)
  const roleCode =
    normalized.role_code ??
    normalized.roleCode ??
    normalized.role_id ??
    normalized.roleId ??
    normalized.role?.code
  const explicitAccountType = normalizeAccountType(
    normalized.account_type ??
      normalized.accountType ??
      normalized.identity_type ??
      normalized.identityType ??
      roleCode
  )
  const tenantType = normalizeTenantType(
    normalized.tenant_type ??
      normalized.tenantType ??
      normalized.tenant?.tenant_type ??
      normalized.tenant?.tenantType
  )
  const planType = String(
    normalized.plan_type ??
      normalized.planType ??
      normalized.tenant?.plan_type ??
      normalized.tenant?.planType ??
      ''
  )
    .trim()
    .toLowerCase()

  const isSuperuser = explicitAccountType === 'superuser' || normalizeBoolean(
    normalized.is_superuser ?? normalized.isSuperuser
  )
  const isEnterpriseAdmin = explicitAccountType === 'enterprise_admin' || normalizeBoolean(
    normalized.is_enterprise_admin ?? normalized.isEnterpriseAdmin
  )
  const isEnterpriseSubUser = explicitAccountType === 'enterprise_sub_user'
  const isPersonalIndependent = explicitAccountType === 'personal_independent'
  const isBusinessTenant = tenantType === 'business' || isEnterpriseAdmin || isEnterpriseSubUser
  const isPersonalTenant = tenantType === 'personal' || isPersonalIndependent
  const accountType = explicitAccountType || (
    isSuperuser
      ? 'superuser'
      : isEnterpriseAdmin
        ? 'enterprise_admin'
        : isBusinessTenant
          ? 'enterprise_sub_user'
          : 'personal_independent'
  )
  const isEnterpriseUser = isSuperuser || isEnterpriseAdmin || isEnterpriseSubUser || isBusinessTenant

  return {
    ...normalized,
    accountType,
    account_type: accountType,
    identityType: accountType,
    identity_type: accountType,
    tenantType,
    planType,
    isSuperuser,
    isEnterpriseAdmin,
    isEnterpriseSubUser,
    isPersonalIndependent,
    isBusinessTenant,
    isPersonalTenant,
    isEnterpriseUser,
    canAccessSystemSettings: isSuperuser || isEnterpriseAdmin
  }
}

export function getCurrentUserInfo() {
  try {
    return normalizeUserInfo(JSON.parse(localStorage.getItem('userInfo') || '{}') || {})
  } catch (error) {
    return normalizeUserInfo({})
  }
}

export function getCurrentUserScope() {
  const userInfo = getCurrentUserInfo()
  return {
    tenant_id: userInfo.tenant_id,
    user_id: userInfo.user_id,
    department_id: userInfo.department_id
  }
}

export function addUserScopeToUrl(url) {
  const scope = getCurrentUserScope()
  try {
    const base = globalThis.location?.origin || 'http://localhost'
    const parsed = new URL(url, base)
    parsed.searchParams.set('tenant_id', scope.tenant_id)
    parsed.searchParams.set('user_id', scope.user_id)
    return /^[a-z][a-z\d+\-.]*:/i.test(url)
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch (error) {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}tenant_id=${encodeURIComponent(scope.tenant_id)}&user_id=${encodeURIComponent(scope.user_id)}`
  }
}
